import React, { useState, useCallback, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useApolloClient } from "react-apollo";
import ReactSelectAsync from "react-select/async";
import { Field, Control, Label, Generic, Help } from "rbx";
import { ALL_ROUTES_QUERY } from "../../graphql/Routes/Routes.graphql";
import { ourToast as toast } from "../../utils";
import { ModalContext } from "../ModalProvider";

const parseRouteForSelect = (Route, name) => ({
  value: Route.RecId,
  name,
  label: `${Route?.RouteId} - ${Route?.PickupLocation?.City}, ${Route?.PickupLocation?.State} / ${Route?.DeliveryLocation?.City}, ${Route?.DeliveryLocation?.State}, ${Route?.RouteDescription}`
});

const RouteSelect = ({
  label,
  size,
  name,
  onChange,
  disabled,
  value,
  isLoading,
  required,
  showHelp,
  variables,
  where,
  addon,
  addons
}) => {
  const client = useApolloClient();
  const [selectLoading, setLoading] = useState(isLoading);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [lastFilters, setLastFilters] = useState(null);
  const { setModalOpen } = useContext(ModalContext);

  const loadDefaultOptions = useCallback(async () => {
    try {
      setLoading(true);
      const routesResult = await client.query({
        query: ALL_ROUTES_QUERY,
        variables: variables || {
          where,
          orderByMulti: "RouteId_ASC"
        }
      });

      const routes = routesResult?.data?.Routes;

      if (Array.isArray(routes)) {
        const grouped = routes.map(route => parseRouteForSelect(route, name));

        setDefaultOptions(grouped);
      }
    } catch (err) {
      toast.error("Error getting Routes.");
    } finally {
      setLoading(false);
    }
  }, [client, name, variables, where]);

  useEffect(() => {
    if (JSON.stringify(variables) === JSON.stringify(lastFilters)) {
      setLastFilters(variables);
      loadDefaultOptions();
    } else {
      loadDefaultOptions();
    }
  }, [lastFilters, loadDefaultOptions, variables]);

  const loadingOptions = useCallback(
    async (inputValue = "") =>
      new Promise(async resolve => {
        try {
          setLoading(true);
          const updatedVariables = {
            ...variables,
            where: {
              ...variables.where,
              OR: [{ RouteId_contains: inputValue }]
            }
          };
          const routesResult = await client.query({
            query: ALL_ROUTES_QUERY,
            variables: updatedVariables,
            fetchPolicy: "cache-first"
          });

          const routes = routesResult?.data?.Routes;
          if (Array.isArray(routes)) {
            const grouped = routesResult.data.Routes.map(route =>
              parseRouteForSelect(route, name)
            );
            resolve(grouped);
          }
        } catch (err) {
          toast.error("Error getting Items.");
        } finally {
          setLoading(false);
        }
      }),
    [client, name, variables]
  );
  return (
    <Field>
      {showHelp && value ? (
        <Help as={Label} style={{ marginTop: 0 }}>
          <Generic
            as={Link}
            tabIndex="-1"
            textColor="link"
            to={`/dispatch/routes/${value?.value}/details`}
            onClick={() => setModalOpen(false)}
          >
            {label}
          </Generic>
        </Help>
      ) : (
        <Label size={size}>{label}</Label>
      )}
      <Field kind="group">
        <Control
          expanded
          size={size}
          style={{
            border:
              required &&
              (!value || value === "" || value === "0" || value === 0)
                ? "1px #e63362 solid"
                : "",
            borderRadius: "4px",
            boxShadow:
              required &&
              (!value || value === "" || value === "0" || value === 0)
                ? "0 0 0 0.125em rgba(230, 51, 98, 0.25)"
                : "",
            marginRight: 0
          }}
        >
          <ReactSelectAsync
            cacheOptions={false}
            color="danger"
            defaultOptions={defaultOptions}
            isDisabled={disabled}
            isLoading={selectLoading}
            loadOptions={loadingOptions}
            name={name}
            placeholder="Start typing..."
            styles={{
              menu: provided => ({
                ...provided,
                zIndex: 999
              })
            }}
            value={value}
            onChange={onChange}
          />
        </Control>
        {!!addon && <Control style={{ margin: 0 }}>{addon}</Control>}
        {!!addons.length &&
          addons.map(item => <Control style={{ margin: 0 }}>{item}</Control>)}
      </Field>
    </Field>
  );
};

RouteSelect.propTypes = {
  value: PropTypes.object,
  name: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  required: PropTypes.bool,
  showHelp: PropTypes.bool,
  variables: PropTypes.object,
  where: PropTypes.object,
  addon: PropTypes.node,
  addons: PropTypes.array
};
RouteSelect.defaultOptions = {
  isLoading: null,
  addons: []
};
export default RouteSelect;
